/**
 * /orders/kitchen data layer (Plan 16).
 *
 * REST + TanStack Query hooks for the chef-side kitchen surface:
 *   - my-kitchens                — kitchen Locations the actor admins
 *   - shipments                  — kitchen-scoped shipments in a date range
 *   - blocking-tasks             — open SHIPMENT-subject tasks in range
 *   - order-statuses             — static enum surface
 *   - cancel shipment            — ORDO_ADMIN-only mutation
 *   - prep-packet (aggregator)   — printable packet data
 *
 * Mirrors the patterns established by `school-orders.ts` (Plan 11)
 * and `location-settings.ts` (Plan 13). The server primitive is
 * `KitchenOrdersManager`; the same primitive backs the GraphQL
 * read queries for the AI tool.
 */

import type { BulkPackingSpec } from '@ordo/constants/packing-plan'
import { shipmentDayKey } from '@ordo/constants/shipment-date-axis'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, apiFetch, apiFetchBlob } from './http'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type KitchenOrderStatus = 'ORDERED' | 'CANCELLED'

export interface KitchenLocation {
  id: string
  name: string
  timeZone: string
  organizationId: string
  profileCodes: Array<'CHEF_ADMIN' | 'CHEF_STAFF' | 'SUPPLIER_ADMIN' | 'SUPPLIER_STAFF'>
}

export interface MyKitchensResponse {
  me: {
    id: string
    kitchenLocations: KitchenLocation[]
  } | null
}

/** Quantity/unit are null when the row has no usable amount (name-only render). */
export interface KitchenLineRecipeIngredient {
  id: string
  name: string
  quantity: number | null
  unit: string | null
  /** RECIPE_YIELD (whole-batch) vs PER_SERVING — drives client scaling. */
  quantityBasis: string | null
  /** Grams per US cup (USDA-derived or hand-set); null = show the weight. */
  gramsPerCup?: number | null
  /** Plan 28 non-linear scaling cap — shown as an advisory beside the scaled
   *  amount, never used to clamp it (the cap is "per batch/roasting pan" and
   *  the pan count is unknown here). */
  maxQuantityValue?: number | null
  maxQuantityUnit?: string | null
  scalingNotes?: string | null
  /** Resolved brand label (pin → kitchen selection → variety → default → null). */
  brand: string | null
  /** The catalog ingredient this line lists — what a chef picks a brand FOR. */
  ingredientId: string
  /** The option `brand` resolved to; null for variety/generic lines. */
  brandOptionId: string | null
  /** Which decision produced `brand`, so the UI can say why it's fixed. */
  brandSource: 'PINNED' | 'KITCHEN' | 'VARIETY' | 'DEFAULT' | null
  /** False when a recipe-line pin makes the brand non-negotiable. */
  brandSelectable: boolean
  /** How many approved brands this ingredient has. 0 or 1 = no choice to offer. */
  brandOptionCount: number
}

export interface KitchenLineRecipeStep {
  id: string
  text: string
  ingredientRefs: Array<{ name: string; quantity: number | null; unit: string | null; quantityBasis: string | null }>
}

export interface KitchenLineRecipe {
  id: string
  baseYield: number
  yieldUnit: string
  ingredients: KitchenLineRecipeIngredient[]
  steps: KitchenLineRecipeStep[]
  haccp: Array<{ id: string; text: string }>
  tips: Array<{ id: string; text: string }>
}

/** What a packaging SKU IS for the chef's pull list (mirrors the server). */
export type ServiceMaterialKind = 'UTENSIL' | 'PLATE' | 'TRAY_BOAT' | 'BAG' | 'CONTAINER' | 'OTHER'

export interface KitchenShipmentLineSummary {
  id: string
  lineRole: 'SERVING' | 'PACKAGE'
  /** Bulk PACKAGING identity (one pan serving many). Optional: cached
   *  pre-field payloads omit it. */
  isBulk?: boolean
  /** Containing package line — à-la-carte packaging lines carry no
   * bundleDeliverableId, so grouping goes through this link instead. */
  parentLineId: string | null
  componentId: string | null
  componentName: string | null
  /** Catalog `ComponentKind` (MAIN_ENTREE / SIDE / BEVERAGE / …). */
  componentKind: string | null
  /** Resolved default ComponentVariant — part of the chef merge key. */
  componentVariantId: string | null
  /** Cover image for the deliverable detail panel. */
  componentImageUri: string | null
  /** Resolved per-grade portion (e.g. "0.75 cup") — part of the merge key. */
  servingSize: string | null
  /** RECIPE-based serving amount (PROD-173): the deliverable pick's
   * serving-size option, else the recipe's primary serving. */
  recipeServingQuantity: number | null
  /** Unit paired with `recipeServingQuantity` (e.g. "Pancakes", "Cup"). */
  recipeServingUnit: string | null
  /** The serving size's own name, its note ("Package"); absent on older payloads. */
  recipeServingName?: string | null
  disherSize?: string | null
  /** Ingredient demand scales by this; 1 = ships at the recipe's primary serving. */
  servingMultiplier?: number
  /** Client `Diet` values derived from attributes + allergen tags. */
  diets: string[]
  /**
   * Reviewed allergens by name ("Milk", "Wheat"), the component's own and its ingredients' (the Contains rollup);
   * absent on cached pre-field payloads.
   */
  allergens?: string[]
  /** Every source of the component has reviewed Contains evidence: an empty list then means none, not "not on file". */
  allergensComplete?: boolean
  /** Full recipe for the detail panel + print pages, or null when none on file. */
  recipe: KitchenLineRecipe | null
  bundleId: string | null
  bundleName: string | null
  /** Logistics tag slugs on the bundle (e.g. `field-trip`) — chef badges. */
  bundleTagSlugs: string[]
  /** Frozen at materialize time: the originating order line was a
   * field-trip (bagged) menu item — drives the "Bagged {Mealtime}" copy. */
  isFieldTrip?: boolean
  /** SUPPLY line — a disposable shipped with the meal (tray/boat, cutlery,
   * plate), distinct from the packaging the food is served in. */
  isSupply?: boolean
  /** Non-null => operator-added non-order item (PROD-1250). */
  extraLineId?: string | null
  /** Non-null => derived holiday treat (free with every meal); never a meal. */
  holidayTreatMenuItemId?: string | null
  bundleDeliverableId: string | null
  bundleDeliverableName: string | null
  packagingSkuId: string | null
  packagingSkuName: string | null
  /** Reference photo for the packaging SKU (linked `PACKAGING` asset). */
  packagingImageUri?: string | null
  /** COMPOSTABLE is shown only for a compostable SKU at a school that explicitly requires it. */
  packagingVariantTag?: 'STANDARD' | 'COMPOSTABLE' | null
  /** Server-derived service-material kind of the packaging SKU (utensils /
   * plates / trays & boats / containers). Null on serving lines. */
  packagingKind?: ServiceMaterialKind | null
  /** `Ingredient.packagingSkuKind` of the packaging SKU (INDIVIDUAL / BULK /
   * UTENSIL / SERVICE). INDIVIDUAL makes the row count a container count; BULK
   * makes it servings in a shared vessel. Null on serving lines. Optional:
   * cached pre-field payloads omit it. */
  packagingSkuKind?: string | null
  quantityBasis: unknown | null
  /** HOT / CHILLED / AMBIENT from the line's BundleDeliverable. */
  temperatureClass: 'HOT' | 'CHILLED' | 'AMBIENT' | null
  /** `Component.milkOptionCode` — non-null marks a milk SKU line. Paired with
   * the shipment's school milk-schedule flag it identifies the weekly milk
   * drop (rendered as the campus card's "Weekly milk" section). */
  milkOptionCode: string | null
  plannedQuantity: number
  /** Grade tier this portion was resolved for (`{ code, label }`, label =
   * "K-8" / "9-12" / "PK3-5"). Null when the line carries no tier stamp or it
   * no longer resolves; optional because cached pre-field payloads omit it. */
  portionTier?: KitchenPortionTier | null
  /** How this PACKAGE line packs (PROD-1500, `@ordo/constants/packing-plan`):
   *  its container and the servings that fit in one, worked out server-side
   *  from the catalog sizes (the container's capacity, each food's portion
   *  volume). Set only on a bulk package line (a root bulk
   *  package, or a Section under one) whose container and portions are all
   *  sized; null everywhere else, and on a bulk line with a gap (the row keeps
   *  the servings-only display). Sum the servings of every line packed the
   *  same way (`bulkPackingSpecKey`) per delivery, then `planBulkPacking` once:
   *  never per line. Optional: cached pre-field payloads omit it. */
  bulkPacking?: BulkPackingSpec | null
  /** True on every bulk package line that could take a spec (a root bulk
   *  package with a container, or a Section under one, holding food), with or
   *  without one. A row shows containers only when every such line feeding it
   *  has a spec. Optional: cached pre-field payloads omit it. */
  bulkPackingTarget?: boolean
}

/** Mirrors the server's `KitchenPortionTier`. */
export interface KitchenPortionTier {
  code: string
  label: string
}

export interface KitchenShipmentDestinationAddress {
  line1: string
  city: string
  stateProvince: string
  postalCode: string
}

/** Destination school's order-deadline policy — drives the chef calendar's per-school color codes + countdown. */
export interface KitchenShipmentDestinationDeadline {
  /** `FIXED_WEEKLY` or `RELATIVE_DAYS`. Absent from pre-field server responses (read as FIXED_WEEKLY). */
  orderDeadlineMode?: string | null
  orderDeadlineDayOfWeek: string | null
  orderDeadlineWeeksBefore: number
  orderDeadlineTime: string
  /** RELATIVE_DAYS only: calendar days before the service date. */
  orderDeadlineRelativeDays?: number | null
  timeZone: string
}

/**
 * The order-group a shipment was materialized for (PROD-1296). A campus that
 * orders by AGE_GROUP ships one shipment per band ("Preschool", "K-8"), and
 * the kitchen packs the bands separately, so the chef surfaces section each
 * mealtime by this. `label` is resolved server-side (the band's custom name,
 * else its grade-range label; the vocabulary value for other dimensions).
 */
export interface KitchenShipmentGroup {
  dimension: string
  value: string
  label: string
  /** Band display order; null when the dimension has no authored order. */
  sortOrder: number | null
}

/** A special-diet meal on a shipment (`KitchenShipmentSummary.specialDiets` on the server). */
export interface SpecialDietItem {
  name: string
  quantity: number
  /** "no peanut", "gluten free": the accommodation's restricted allergens, then its dietary needs. */
  restrictions: string[]
}

export interface KitchenShipmentSummary {
  menuInstructions?: Array<{ menuId: string; menuName: string; delivery: string | null; service: string | null }>
  /** Every menu on the shipment is delivered cold and reheated on site; absent on cached pre-field payloads. */
  deliverCold?: boolean
  /** The shipment's special-diet meals, named as served; absent when none (or on cached pre-field payloads). */
  specialDiets?: SpecialDietItem[]

  id: string
  vendorLocationId: string
  schoolLocationId: string
  serviceDate: string
  deliveryDate: string
  mealtime: string
  state: KitchenOrderStatus
  /** Order-group stamp + label. Null on an ungrouped shipment; absent from
   *  pre-field server responses (treated as ungrouped). */
  group?: KitchenShipmentGroup | null
  /** Chef DRY RUN — a pre-launch test cook, not live service. */
  isDryRun: boolean
  /** TASTING vs DRY_RUN when `isDryRun` — names the badge/banner. Null on
   *  live-service shipments; absent from pre-field server responses. */
  dryRunServiceType?: 'DRY_RUN' | 'TASTING' | null
  totalServings: number
  destinationSchoolName: string
  destinationSchoolOrganizationName: string | null
  destinationSchoolAddressFormatted: string | null
  destinationSchoolAddress: KitchenShipmentDestinationAddress | null
  /** Destination school's recurring order-deadline policy (for the calendar). */
  destinationSchoolDeadline: KitchenShipmentDestinationDeadline
  /** `Location.milkDeliveryDayOfWeek` — non-null means the campus takes milk
   * on a fixed weekly delivery day, so milk-stamped standalone lines belong
   * to the weekly milk drop rather than the day's meals. */
  destinationSchoolMilkDeliveryDayOfWeek: string | null
  /** From the school Location's Address row (e.g. "Drop-off between …"). */
  deliveryWindow: string | null
  contactName: string | null
  contactPhone: string | null
  parkingInstructions: string | null
  lines: KitchenShipmentLineSummary[]
}

/** A school this kitchen serves (via menu assignment) + its deadline policy. */
export interface KitchenServedSchoolDeadline {
  schoolLocationId: string
  deadline: KitchenShipmentDestinationDeadline
}

/** A served school day the calendar/menu service pattern marks as intentionally no-service. */
export interface KitchenServedSchoolNoServiceDay {
  schoolLocationId: string
  date: string
  notes: string | null
}

/** One overdue temp-log day on the orders gate. */
export interface TempLogGateDay {
  /** `YYYY-MM-DD` delivery day the open logs belong to. */
  dayKey: string
  dueAt: string | null
  openTaskCount: number
  totalTaskCount: number
  /**
   * Presence of an open task — drives whether the gate card offers "Log
   * temperatures" or the plain `/tasks` fallback. NOT the deep-link id:
   * the workflow route is keyed by DAY (`tempLogTaskIdForDay(dayKey)`),
   * and routing it through this raw cuid was PROD-1164.
   */
  firstOpenTaskId: string | null
}

export interface KitchenShipmentsResponse {
  shipments: KitchenShipmentSummary[]
  /**
   * Present when the chef has OVERDUE temperature logs (delivery days
   * before the kitchen's today with open CHEF_SUBMIT_TEMP_LOG tasks).
   * The server withholds all order data until the logs are complete —
   * `shipments` arrives empty and the page renders the gate screen.
   */
  tempLogGate?: { overdueDays: TempLogGateDay[] } | null
  /**
   * Schools the kitchen is set up to serve regardless of whether any orders
   * exist yet — seeds the order calendar's open/closed-week model so a
   * cold-start kitchen (menus assigned, nothing ordered) shows open weeks.
   */
  servedSchoolDeadlines: KitchenServedSchoolDeadline[]
  /**
   * Range-scoped no-service dates for served schools. Distinguishes
   * intentionally empty school-service days from open windows with no orders yet.
   */
  servedSchoolNoServiceDays?: KitchenServedSchoolNoServiceDay[]
}

export interface KitchenBlockingTaskSummary {
  id: string
  category: string
  subKind: string | null
  title: string
  description: string | null
  status: string
  dueAt: string | null
  blocksSubjectKind: string
  blocksSubjectId: string | null
  organizationId: string
}

export interface KitchenBlockingTasksResponse {
  tasks: KitchenBlockingTaskSummary[]
}

export interface PrepPacketSections {
  packagingSlips: boolean
  scaledRecipes: boolean
  scaledIngredients: boolean
  tempLogColumns: boolean
}

export interface PrepPacketComponentTotal {
  componentId: string
  componentName: string
  totalServings: number
}

export interface PrepPacketTempLogRow {
  schoolLocationId: string
  schoolName: string
  /** DELIVERY day (`YYYY-MM-DD`) — the temp-log sheet rides the truck. */
  deliveryDate: string
  mealtime: string
}

export interface PrepPacketResponse {
  kitchenLocationId: string
  startDate: string
  endDate: string
  sections: PrepPacketSections
  shipments: KitchenShipmentSummary[]
  componentTotals: PrepPacketComponentTotal[]
  ingredientTotals: PrepPacketComponentTotal[]
  tempLogRows: PrepPacketTempLogRow[]
}

// ---------------------------------------------------------------------------
// Pure helpers — exported so the page module + tests can read them.
// ---------------------------------------------------------------------------

export const DEFAULT_PREP_PACKET_SECTIONS: PrepPacketSections = {
  packagingSlips: true,
  scaledRecipes: true,
  scaledIngredients: true,
  tempLogColumns: true,
}

/**
 * Format `YYYY-MM-DD` from a Date in UTC. Used for the date-nav
 * query-string + the day-key bucketing in the week calendar.
 */
export function ymdUtc(d: Date): string {
  const y = d.getUTCFullYear().toString().padStart(4, '0')
  const m = (d.getUTCMonth() + 1).toString().padStart(2, '0')
  const dd = d.getUTCDate().toString().padStart(2, '0')
  return `${y}-${m}-${dd}`
}

/**
 * Monday-anchored start-of-week for a given date in UTC. Weekends snap
 * FORWARD to the upcoming Monday — same service-week rule as
 * `startOfWorkWeek` in chefs/service-days.
 */
export function startOfWeekMondayUtc(d: Date): Date {
  const out = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  const dow = out.getUTCDay()
  const delta = dow === 0 ? 1 : dow === 6 ? 2 : 1 - dow
  out.setUTCDate(out.getUTCDate() + delta)
  return out
}

/** Add `n` days (UTC) to a date — pure / no mutation. */
export function addDaysUtc(d: Date, n: number): Date {
  const out = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  out.setUTCDate(out.getUTCDate() + n)
  return out
}

/**
 * Build the M-F days array for the week starting at `weekStart`.
 * Pure; used by the week-calendar component.
 */
export function weekdaysFromMondayUtc(weekStart: Date): Date[] {
  return Array.from({ length: 5 }, (_, i) => addDaysUtc(weekStart, i))
}

/**
 * Group shipments by school+ymd for the week calendar render.
 * Pure for unit-test coverage.
 *
 * DELIVERY axis: this is a KITCHEN calendar, and a kitchen's day is the day
 * food leaves the building. Bucketing on `serviceDate` put a Friday-delivered
 * bagged lunch in Monday's column — three days after the chef had to cook it.
 */
export function groupShipmentsBySchoolAndDay(
  shipments: ReadonlyArray<KitchenShipmentSummary>,
): Map<string, Map<string, KitchenShipmentSummary[]>> {
  const out = new Map<string, Map<string, KitchenShipmentSummary[]>>()
  for (const s of shipments) {
    if (!out.has(s.schoolLocationId)) {
      out.set(s.schoolLocationId, new Map())
    }
    const dayMap = out.get(s.schoolLocationId)!
    const dayKey = shipmentDayKey(s, 'DELIVERY')
    if (!dayMap.has(dayKey)) {
      dayMap.set(dayKey, [])
    }
    dayMap.get(dayKey)!.push(s)
  }
  return out
}

export function isEveryServedSchoolNoServiceOnDate(
  servedSchools: ReadonlyArray<KitchenServedSchoolDeadline>,
  noServiceDays: ReadonlyArray<KitchenServedSchoolNoServiceDay>,
  dateKey: string,
): boolean {
  if (servedSchools.length === 0) {
    return false
  }
  const noServiceSchoolIds = new Set(
    noServiceDays.filter((day) => day.date.slice(0, 10) === dateKey).map((day) => day.schoolLocationId),
  )
  return servedSchools.every((school) => noServiceSchoolIds.has(school.schoolLocationId))
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchMyKitchens(signal?: AbortSignal): Promise<MyKitchensResponse> {
  return apiFetch<MyKitchensResponse>('/api/kitchen-orders/my-kitchens', { signal })
}

export interface ShipmentsRangeVariables {
  kitchenLocationId: string
  startDate: string
  endDate: string
  includeCancelled?: boolean
}

export async function fetchKitchenShipments(
  vars: ShipmentsRangeVariables,
  signal?: AbortSignal,
): Promise<KitchenShipmentsResponse> {
  return apiFetch<KitchenShipmentsResponse>('/api/kitchen-orders/shipments', {
    query: {
      kitchenLocationId: vars.kitchenLocationId,
      startDate: vars.startDate,
      endDate: vars.endDate,
      ...(vars.includeCancelled ? { includeCancelled: true } : {}),
    },
    signal,
  })
}

export async function fetchKitchenBlockingTasks(
  vars: ShipmentsRangeVariables,
  signal?: AbortSignal,
): Promise<KitchenBlockingTasksResponse> {
  return apiFetch<KitchenBlockingTasksResponse>('/api/kitchen-orders/blocking-tasks', {
    query: {
      kitchenLocationId: vars.kitchenLocationId,
      startDate: vars.startDate,
      endDate: vars.endDate,
    },
    signal,
  })
}

export interface NextOrderDateVariables {
  kitchenLocationId: string
  /** ISO date — the returned date is strictly after this one. */
  after: string
}

export interface NextOrderDateResponse {
  /** ISO datetime of the next serviceDate with live orders, or null. */
  nextDate: string | null
}

export async function fetchNextOrderDate(
  vars: NextOrderDateVariables,
  signal?: AbortSignal,
): Promise<NextOrderDateResponse> {
  return apiFetch<NextOrderDateResponse>('/api/kitchen-orders/next-order-date', {
    query: {
      kitchenLocationId: vars.kitchenLocationId,
      after: vars.after,
    },
    signal,
  })
}

export interface CancelShipmentVariables {
  shipmentId: string
  reason: string | null
}

export interface CancelShipmentResponse {
  id: string
  state: KitchenOrderStatus
}

export async function cancelShipment(vars: CancelShipmentVariables): Promise<CancelShipmentResponse> {
  return apiFetch<CancelShipmentResponse>(
    `/api/kitchen-orders/shipments/${encodeURIComponent(vars.shipmentId)}/cancel`,
    {
      method: 'POST',
      body: { reason: vars.reason },
    },
  )
}

// ---------------------------------------------------------------------------
// Chef pre-approved substitutions (order-local)
// ---------------------------------------------------------------------------

/** One pre-approved alternate the chef may swap a component to. */
export interface ChefSubstituteOption {
  ruleId: string
  replacementComponentId: string
  replacementName: string
  /** Denormalized portion label off the rule (e.g. "0.5 cup"), when set. */
  replacementServingSizeLabel: string | null
  scopeType: string
  scopeLabel: string
}

export interface EligibleSubstitutesResponse {
  shipmentId: string
  componentId: string
  substitutes: ChefSubstituteOption[]
}

export interface EligibleSubstitutesVariables {
  shipmentId: string
  componentId: string
}

export async function fetchEligibleSubstitutes(
  vars: EligibleSubstitutesVariables,
  signal?: AbortSignal,
): Promise<EligibleSubstitutesResponse> {
  return apiFetch<EligibleSubstitutesResponse>(
    `/api/kitchen-orders/shipments/${encodeURIComponent(vars.shipmentId)}/substitutes`,
    { query: { componentId: vars.componentId }, signal },
  )
}

export interface ApplySubstitutionVariables {
  shipmentId: string
  componentId: string
  ruleId: string
}

export interface ApplySubstitutionResponse {
  shipmentId: string
  fromComponentId: string
  toComponentId: string
  updatedLineIds: string[]
}

export async function applySubstitution(vars: ApplySubstitutionVariables): Promise<ApplySubstitutionResponse> {
  return apiFetch<ApplySubstitutionResponse>(
    `/api/kitchen-orders/shipments/${encodeURIComponent(vars.shipmentId)}/substitutes`,
    { method: 'POST', body: { componentId: vars.componentId, ruleId: vars.ruleId } },
  )
}

export interface PrepPacketVariables {
  kitchenLocationId: string
  startDate: string
  endDate: string
  sections: PrepPacketSections
}

export async function fetchPrepPacket(vars: PrepPacketVariables, signal?: AbortSignal): Promise<PrepPacketResponse> {
  return apiFetch<PrepPacketResponse>('/api/kitchen-orders/prep-packet', {
    query: {
      kitchenLocationId: vars.kitchenLocationId,
      startDate: vars.startDate,
      endDate: vars.endDate,
      sections: JSON.stringify(vars.sections),
    },
    signal,
  })
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const myKitchensQueryKey = ['kitchen-orders', 'my-kitchens'] as const

export const kitchenShipmentsQueryKey = (vars: ShipmentsRangeVariables) =>
  ['kitchen-orders', 'shipments', vars] as const

export const kitchenBlockingTasksQueryKey = (vars: ShipmentsRangeVariables) =>
  ['kitchen-orders', 'blocking-tasks', vars] as const

export const prepPacketQueryKey = (vars: PrepPacketVariables) => ['kitchen-orders', 'prep-packet', vars] as const

export const nextOrderDateQueryKey = (vars: NextOrderDateVariables) =>
  ['kitchen-orders', 'next-order-date', vars] as const

export const eligibleSubstitutesQueryKey = (vars: EligibleSubstitutesVariables) =>
  ['kitchen-orders', 'substitutes', vars] as const

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useMyKitchensQuery(opts: { enabled?: boolean } = {}) {
  return useQuery<MyKitchensResponse>({
    queryKey: myKitchensQueryKey,
    queryFn: ({ signal }) => fetchMyKitchens(signal),
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
  })
}

export function useKitchenShipmentsQuery(vars: ShipmentsRangeVariables, opts: { enabled?: boolean } = {}) {
  return useQuery<KitchenShipmentsResponse>({
    queryKey: kitchenShipmentsQueryKey(vars),
    queryFn: ({ signal }) => fetchKitchenShipments(vars, signal),
    enabled: (opts.enabled ?? true) && Boolean(vars.kitchenLocationId),
    // 30s polling matches Plan 11 — the chef refreshes through the
    // day naturally; bumping to 5m on the week view feels stale.
    staleTime: 30_000,
    refetchInterval: 30_000,
  })
}

export function useKitchenBlockingTasksQuery(vars: ShipmentsRangeVariables, opts: { enabled?: boolean } = {}) {
  return useQuery<KitchenBlockingTasksResponse>({
    queryKey: kitchenBlockingTasksQueryKey(vars),
    queryFn: ({ signal }) => fetchKitchenBlockingTasks(vars, signal),
    enabled: (opts.enabled ?? true) && Boolean(vars.kitchenLocationId),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })
}

export function useCancelShipmentMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: cancelShipment,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kitchen-orders', 'shipments'] })
      void qc.invalidateQueries({ queryKey: ['kitchen-orders', 'blocking-tasks'] })
    },
  })
}

/**
 * Next serviceDate (strictly after `vars.after`) with live orders for
 * this kitchen — the empty state's "Go to next day with orders" jump
 * (PROD-178). Callers gate `enabled` so this only fires while the
 * empty state is actually visible.
 */
export function useNextOrderDateQuery(vars: NextOrderDateVariables, opts: { enabled?: boolean } = {}) {
  return useQuery<NextOrderDateResponse>({
    queryKey: nextOrderDateQueryKey(vars),
    queryFn: ({ signal }) => fetchNextOrderDate(vars, signal),
    enabled: (opts.enabled ?? true) && Boolean(vars.kitchenLocationId),
    staleTime: 30_000,
  })
}

/**
 * Pre-approved alternates for one component on one shipment. Callers gate
 * `enabled` so the fetch fires only while the swap picker is open.
 */
export function useEligibleSubstitutesQuery(vars: EligibleSubstitutesVariables, opts: { enabled?: boolean } = {}) {
  return useQuery<EligibleSubstitutesResponse>({
    queryKey: eligibleSubstitutesQueryKey(vars),
    queryFn: ({ signal }) => fetchEligibleSubstitutes(vars, signal),
    enabled: (opts.enabled ?? true) && Boolean(vars.shipmentId) && Boolean(vars.componentId),
    staleTime: 30_000,
  })
}

export function useApplySubstitutionMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: applySubstitution,
    onSuccess: () => {
      // The swap rewrites shipment lines — every kitchen read is stale.
      void qc.invalidateQueries({ queryKey: ['kitchen-orders', 'shipments'] })
      void qc.invalidateQueries({ queryKey: ['kitchen-orders', 'prep-packet'] })
      void qc.invalidateQueries({ queryKey: ['kitchen-orders', 'substitutes'] })
      // The shopping list aggregates those same lines' recipes server-side,
      // and its Recipes tab folds the shipments — both must refetch.
      void qc.invalidateQueries({ queryKey: ['shopping-list'] })
    },
  })
}

export function usePrepPacketQuery(vars: PrepPacketVariables, opts: { enabled?: boolean } = {}) {
  return useQuery<PrepPacketResponse>({
    queryKey: prepPacketQueryKey(vars),
    queryFn: ({ signal }) => fetchPrepPacket(vars, signal),
    enabled: (opts.enabled ?? true) && Boolean(vars.kitchenLocationId),
    staleTime: 5 * 60_000, // longer cache — the chef won't click "generate" twice in a row
  })
}

// ---------------------------------------------------------------------------
// Print Orders packet → PDF (printing-press)
// ---------------------------------------------------------------------------

export const KITCHEN_PACKET_PDF_PATH = '/api/kitchen-orders/print-packet/pdf'

export interface KitchenPacketPdfRequest {
  kitchenLocationId: string
  /** `printDocumentTitle(target)` — the PDF's name. */
  title: string
  /** The rendered packet (the real print components, measured contents included). */
  markup: string
  /** The page's hashed stylesheet; null under the Vite dev server. */
  cssHref: string | null
  startDate?: string
  endDate?: string
  /** The dialog's measured page count, logged server-side against the real one. */
  expectedPages?: number
}

export interface KitchenPacketPdf {
  blob: Blob
  /** Real page count of the rendered PDF, when the server reported it. */
  pages: number | null
}

/**
 * Render the Print Orders packet as a PDF (iPhone/iPad share it to the share
 * sheet to print). The markup is gzipped on the way up — Tailwind class
 * strings shrink ~10× and chefs print from phones.
 */
export async function fetchKitchenPacketPdf(
  req: KitchenPacketPdfRequest,
  signal?: AbortSignal,
): Promise<KitchenPacketPdf> {
  const { blob, headers } = await apiFetchBlob(KITCHEN_PACKET_PDF_PATH, {
    method: 'POST',
    body: req,
    gzip: true,
    expectContentType: 'application/pdf',
    signal,
  })
  const magic = new Uint8Array(await blob.slice(0, 5).arrayBuffer())
  if (String.fromCharCode(...magic) !== '%PDF-') {
    throw new ApiError(200, { error: 'unexpected_response', message: 'The print came back empty.' })
  }
  const pages = Number(headers.get('X-Packet-Pages'))
  return {
    blob: blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' }),
    pages: Number.isInteger(pages) && pages > 0 ? pages : null,
  }
}
